import type { _Tile2DHeader, GeoBoundingBox } from '@deck.gl/geo-layers';
import type { LayerConfigMap } from './layers/types';
import { TREE_COVER_LAYERS } from './layers/types';

// Computes a point which is the geometric center of mass of a geometry.
export const getCentroid = (polygon: number[][]) => {
  if (!polygon || polygon.length === 0) {
    return [];
  }

  const geoCenter = getGeometricCenter(polygon);

  let sx = 0;
  let sy = 0;
  let sArea = 0;
  let i, pi, pj, xi, xj, yi, yj, a;

  const neutralizedPoints = polygon.map((i) => [
    i[0] - geoCenter[0],
    i[1] - geoCenter[1],
  ]);

  for (i = 0; i < polygon.length - 1; i++) {
    // pi is the current point
    pi = neutralizedPoints[i];
    xi = pi[0];
    yi = pi[1];

    // pj is the next point (pi+1)
    pj = neutralizedPoints[i + 1];
    xj = pj[0];
    yj = pj[1];

    // a is the common factor to compute the signed area and the final coordinates
    a = xi * yj - xj * yi;

    // sArea is the sum used to compute the signed area
    sArea += a;

    // sx and sy are the sums used to compute the final coordinates
    sx += (xi + xj) * a;
    sy += (yi + yj) * a;
  }

  // Shape has no area: fallback on geometric center
  if (sArea === 0) {
    return geoCenter;
  } else {
    // Compute the signed area, and factorize
    const area = sArea * 0.5;
    const areaFactor = 1 / (6 * area);

    // Compute the final coordinates, adding back the values that have been neutralized
    return [geoCenter[0] + areaFactor * sx, geoCenter[1] + areaFactor * sy];
  }
};

export const getGeometricCenter = (polygon: number[][]) => {
  if (!polygon || polygon.length === 0) {
    return [];
  }

  let sumLon = 0;
  let sumLat = 0;

  polygon.forEach((i) => {
    sumLon += i[0];
    sumLat += i[1];
  });

  return [sumLon / polygon.length, sumLat / polygon.length];
};

export const isClientNameLoaded = () => {
  if (window.dataLayer) {
    const gtmHasLoadedClient = window.dataLayer.find((e) => e['clientName']);
    return gtmHasLoadedClient;
  }
};

export const isiOS =
  ['iPad', 'iPhone', 'iPod'].includes(navigator.userAgent) ||
  // iPad on iOS 13 detection
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

export const isTreeCoverActive = (layerConfig: LayerConfigMap) =>
  TREE_COVER_LAYERS.findIndex((l) => layerConfig[l].visible) !== -1;

function isGeoBoundingBox(bbox: unknown): bbox is GeoBoundingBox {
  return (
    typeof bbox === 'object' &&
    bbox !== null &&
    'west' in bbox &&
    'east' in bbox &&
    'north' in bbox &&
    'south' in bbox
  );
}

/**
 * This is a utility function to get properly typed bounding boxes, as the upcoming Deck.GL types marks
 * `tile.bbox` as `TileBoundingBox`:
 *
 * export type GeoBoundingBox = {west: number; north: number; east: number; south: number};
 * export type NonGeoBoundingBox = {left: number; top: number; right: number; bottom: number};
 * export type TileBoundingBox = NonGeoBoundingBox | GeoBoundingBox;
 *
 * This means that the bbox property of a Tile2DHeader can be either a GeoBoundingBox or a NonGeoBoundingBox,
 * which means our geographic directions might not exist as keys.
 */
export function getGeoBoundingBox(props: {
  tile: _Tile2DHeader;
}): GeoBoundingBox | null {
  return isGeoBoundingBox(props.tile.bbox) ? props.tile.bbox : null;
}
